import React, { useEffect, useMemo, useState, useCallback } from 'react';
import useMobileValidation from '../hooks/useMobileValidation';
import useIsMobile from '../hooks/useIsMobile';
import PmsComplianceCheckin from './PmsComplianceCheckin';
import GuestIntelligenceCard from '../components/GuestIntelligenceCard';
import { toDateTimeLocalValue, todayIso, dateTimeLocalToIso } from '../utils/dateUtils';
import { buildGuestPortalUrl } from '../utils/guestPortalUrl';
import LockEncodeModal from '../components/LockEncodeModal';
const normalizeCountry = (value) => String(value || '').trim().toLowerCase();
const buildDefaultComplianceData = (hotelCountry, guestName = '') => ({
  guest_name: guestName,
  nationality: hotelCountry || 'India',
  is_foreign_guest: false
});

const buildGuestList = (hotelCountry, primaryName, totalAdults, eachGuest) => {
  const count = (eachGuest && totalAdults > 1) ? totalAdults : 1;
  return Array.from({ length: count }, (_, i) =>
    buildDefaultComplianceData(hotelCountry, i === 0 ? primaryName : '')
  );
};

const buildDefaultLine = () => ({
  room_type_id: '', // Always blank by default so all room types are selectable
  room_id: '',
  adults: 1,
  children: 0,
  rate_plan_code: '',
  tax_mode: '', // '' = rate plan default | 'INCLUSIVE' | 'EXCLUSIVE'
  rate_amount: 0,
  tax_total: 0,
  total_amount: 0,
  nights: 0
});

const buildCountOptions = (min, max) => {
  const start = Number(min);
  const end = Number(max);
  if (!Number.isFinite(start) || !Number.isFinite(end) || end < start) return [];
  const options = [];
  for (let i = start; i <= end; i += 1) options.push(i);
  return options;
};

const clampLineByRoomType = (line, roomType) => {
  if (!roomType) return line;
  const maxAdults = Math.max(1, Number(roomType.max_adults || 1));
  const maxChildren = Math.max(0, Number(roomType.max_children || 0));
  const maxPax = Math.max(1, Number(roomType.max_pax || (maxAdults + maxChildren) || 1));
  let adults = Math.max(1, Math.min(maxPax, Number(line.adults || 1)));
  let children = Math.max(0, Math.min(maxPax, Number(line.children || 0)));
  if ((adults + children) > maxPax) {
    const overflow = (adults + children) - maxPax;
    if (children >= overflow) {
      children -= overflow;
    } else {
      const remaining = overflow - children;
      children = 0;
      adults = Math.max(1, adults - remaining);
    }
  }
  return { ...line, adults, children };
};

const buildDefaultForm = () => {
  const now = new Date();
  const tomorrow = new Date(now);
  tomorrow.setDate(tomorrow.getDate() + 1);
  return {
    arrival_datetime: toDateTimeLocalValue(now),
    departure_datetime: toDateTimeLocalValue(tomorrow),
    guest_name: '',
    mobile_number: '',
    email: '',
    gst_number: '',
    company_name: '',
    deposit_amount: '',
    deposit_payment_mode: 'CASH',
    deposit_reference_no: '',
    deposit_remarks: '',
    lines: [buildDefaultLine()]
  };
};

const WALKIN_LOCKED_ROOM_KEY = 'pms_walkin_locked_room';

const readStoredLockedRoom = () => {
  try {
    const raw = window.localStorage.getItem(WALKIN_LOCKED_ROOM_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.room_id) return null;
    return parsed;
  } catch {
    return null;
  }
};

const storeLockedRoom = (room) => {
  try {
    if (!room?.room_id) {
      window.localStorage.removeItem(WALKIN_LOCKED_ROOM_KEY);
      return;
    }
    window.localStorage.setItem(WALKIN_LOCKED_ROOM_KEY, JSON.stringify(room));
  } catch {
    // ignore storage errors
  }
};

function PmsWalkInCheckIn({ token, role, selectedProperty, prefilledRoom, prefillGuest, onPrefillGuestConsumed, aiGuestRecognitionEnabled }) {
  const { isMobile } = useIsMobile();
  const { validateMobile } = useMobileValidation(token, selectedProperty);
  const [rooms, setRooms] = useState([]);
  const [roomTypes, setRoomTypes] = useState([]);
  const [ratePlans, setRatePlans] = useState([]);
  const [roomTaxPolicyConfigured, setRoomTaxPolicyConfigured] = useState(false);
  const [form, setForm] = useState(buildDefaultForm());
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [guestPortalUrl, setGuestPortalUrl] = useState('');
  const [lockedRoom, setLockedRoom] = useState(null);

  // Compliance step state
  const [step, setStep] = useState(1);
  const [complianceGuestList, setComplianceGuestList] = useState([buildDefaultComplianceData(selectedProperty?.country)]);
  const [activeGuestIdx, setActiveGuestIdx] = useState(0);
  const [fieldRules, setFieldRules] = useState([]);
  const [policyConfig, setPolicyConfig] = useState({});
  const [countryOptions, setCountryOptions] = useState([]);
  const [idTypeOptions, setIdTypeOptions] = useState([]);
  const [complianceErrorsList, setComplianceErrorsList] = useState([{}]);
  const [overrideMode, setOverrideMode] = useState(false);
  const [overrideReason, setOverrideReason] = useState('');

  // Inventory restriction modal state
  const [restrictionModal, setRestrictionModal] = useState(null);
  // Lock-encode modal: shown right after a successful check-in. Auto-fires
  // POST /api/v1/lock/encode-checkin when the user clicks Encode. If no
  // lock agent is online for the property, the modal shows a non-blocking
  // warning and lets the operator skip and encode manually on Onity.
  const [lockEncodeModal, setLockEncodeModal] = useState(null);
  const [restrictionBusy, setRestrictionBusy] = useState(false);

  // Quick Add Rooms state
  const [quickAdd, setQuickAdd] = useState({ room_type_id: '', count: 1, adults: 2, children: 0, rate_plan_code: '' });
  const [quickAddOpen, setQuickAddOpen] = useState(false);
  const [paxTarget, setPaxTarget] = useState({ adults: 0, children: 0 }); // requested totals from Quick Add

  const isRoomLockedFromContext = Boolean(lockedRoom?.room_id);

  const headers = () => ({
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
    'x-chain-id': selectedProperty?.chain_id || '',
    'x-property-id': selectedProperty?.id || ''
  });

  const loadMasters = async () => {
    if (!selectedProperty?.id) {
      setRooms([]);
      setRoomTypes([]);
      setRatePlans([]);
      return;
    }
    try {
      const [roomRes, roomTypeRes, planRes] = await Promise.all([
        fetch('/api/v1/pms/rooms', { headers: headers() }),
        fetch('/api/v1/pms/room-types?all=1', { headers: headers() }),
        fetch('/api/v1/pms/rate-plans?all=1', { headers: headers() })
      ]);
      const roomData = await roomRes.json();
      const roomTypeData = await roomTypeRes.json();
      const planData = await planRes.json();
      if (!roomRes.ok) throw new Error(roomData?.error || 'Failed to load rooms');
      if (!roomTypeRes.ok) throw new Error(roomTypeData?.error || 'Failed to load room types');
      if (!planRes.ok) throw new Error(planData?.error || 'Failed to load rate plans');
      // Bug 8648 — a room can be occupancy=VACANT yet maintenance=BLOCKED /
      // OUT_OF_ORDER / PAINTING. Such rooms must NOT be offered for walk-in
      // check-in (the backend rejects them too, but they shouldn't even appear
      // in the Room dropdown). Mirrors the Command Center availableRooms filter.
      const vacantRooms = Array.isArray(roomData)
        ? roomData.filter((r) => String(r.occupancy_status || '').toUpperCase() === 'VACANT'
            && !['BLOCKED', 'OUT_OF_ORDER', 'PAINTING'].includes(String(r.maintenance_status || '').toUpperCase()))
        : [];
      setRooms(vacantRooms);
      // Only show room types that have at least one vacant room
      const vacantRoomTypeIds = new Set(vacantRooms.map((r) => String(r.room_type_id)));
      const filteredRoomTypes = Array.isArray(roomTypeData)
        ? roomTypeData.filter((r) => r.is_active !== false && vacantRoomTypeIds.has(String(r.room_type_id)))
        : [];
      console.log('Filtered Room Types for Dropdown:', filteredRoomTypes);
      setRoomTypes(filteredRoomTypes);
      setRatePlans(Array.isArray(planData) ? planData.filter((r) => r.is_active !== false) : []);
    } catch (err) {
      setError(err?.message || 'Failed to load Walk-In masters');
    }
  };
  const buildFormForCurrentContext = () => {
    const next = buildDefaultForm();
    if (!isRoomLockedFromContext) return next;
    next.lines = [{ ...buildDefaultLine(), room_id: String(lockedRoom.room_id) }];
    const pickedRoom = rooms.find((r) => String(r.room_id) === String(lockedRoom.room_id));
    if (pickedRoom?.room_type_id) next.lines[0].room_type_id = String(pickedRoom.room_type_id);
    return next;
  };

  useEffect(() => {
    const stored = readStoredLockedRoom();
    const fromContext = prefilledRoom?.room_id
      ? {
          room_id: prefilledRoom.room_id,
          room_number: prefilledRoom.room_number || '',
          ts: prefilledRoom.ts || Date.now(),
          chain_id: selectedProperty?.chain_id || '',
          property_id: selectedProperty?.id || ''
        }
      : null;

    if (fromContext) {
      setLockedRoom(fromContext);
      storeLockedRoom(fromContext);
      return;
    }

    // No prefilledRoom from Command Center — clear any stale lock so screen works standalone
    storeLockedRoom(null);
    setLockedRoom(null);
  }, [prefilledRoom?.room_id, prefilledRoom?.room_number, prefilledRoom?.ts, selectedProperty?.chain_id, selectedProperty?.id]);

  const loadTaxSourceStatus = async () => {
    if (!selectedProperty?.id) {
      setRoomTaxPolicyConfigured(false);
      return;
    }
    try {
      const statusRes = await fetch('/api/v1/pms/tax-source-status', { headers: headers() });
      const statusData = await statusRes.json().catch(() => ({}));
      if (statusRes.ok) {
        setRoomTaxPolicyConfigured(statusData?.room_policy_configured === true);
        return;
      }
      const policyRes = await fetch('/api/room-tax-policy', { headers: headers() });
      const policyData = await policyRes.json().catch(() => ([]));
      if (!policyRes.ok) throw new Error('Failed to load tax source status');
      setRoomTaxPolicyConfigured((Array.isArray(policyData) ? policyData : []).some((row) => row?.is_active !== false));
    } catch (_) {
      setRoomTaxPolicyConfigured(false);
    }
  };

  const loadComplianceFields = useCallback(async () => {
    if (!selectedProperty?.id) return;
    try {
      const [fieldsRes, policyRes, countriesRes, idTypesRes] = await Promise.all([
        fetch('/api/v1/compliance/fields', { headers: headers() }),
        fetch('/api/v1/compliance/policy/guest-identity-document-rules', { headers: headers() }),
        fetch('/api/v1/core/countries', { headers: headers() }),
        fetch('/api/v1/pms/id-types', { headers: headers() })
      ]);
      const [fieldsData, policyData, countriesData, idTypesData] = await Promise.all([
        fieldsRes.json().catch(() => []),
        policyRes.json().catch(() => ({})),
        countriesRes.json().catch(() => []),
        idTypesRes.json().catch(() => [])
      ]);
      if (Array.isArray(fieldsData)) setFieldRules(fieldsData);
      if (policyRes.ok && policyData && typeof policyData === 'object') {
        setPolicyConfig(policyData);
      } else {
        setPolicyConfig({});
      }
      if (Array.isArray(countriesData)) {
        setCountryOptions(countriesData);
      } else {
        setCountryOptions([]);
      }
      setIdTypeOptions(Array.isArray(idTypesData) ? idTypesData : []);
    } catch { /* ignore */ }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProperty?.id, token]);

  const loadPmsConfig = async () => {
    if (!selectedProperty?.id) return;
    try {
      const res = await fetch('/api/v1/pms/property-config', { headers: headers() });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data || data.error) return;
      const coTime = data.default_checkout_time || '11:00';
      const [hh, mm] = coTime.split(':').map(Number);
      // Use property business_date (set by Night Audit) instead of browser date
      const baseDate = (data.business_date && /^\d{4}-\d{2}-\d{2}$/.test(data.business_date))
        ? new Date(data.business_date + 'T00:00:00')
        : new Date();
      const arrNow = new Date(baseDate);
      const nowTime = new Date();
      arrNow.setHours(nowTime.getHours(), nowTime.getMinutes(), 0, 0);
      const dep = new Date(baseDate);
      dep.setDate(dep.getDate() + 1);
      dep.setHours(hh || 11, mm || 0, 0, 0);
      setForm((f) => ({ ...f, arrival_datetime: toDateTimeLocalValue(arrNow), departure_datetime: toDateTimeLocalValue(dep) }));
    } catch { /* ignore */ }
  };

  useEffect(() => {
    setError('');
    setMessage('');
    const freshForm = buildDefaultForm();
    setForm(freshForm);
    setStep(1);
    setComplianceGuestList([buildDefaultComplianceData(selectedProperty?.country)]);
    setActiveGuestIdx(0);
    setComplianceErrorsList([{}]);
    setOverrideMode(false);
    setOverrideReason('');
    loadMasters();
    loadTaxSourceStatus();
    loadComplianceFields();
    loadPmsConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, selectedProperty?.id]);

  // Reset stale arrival/departure dates if the form was left open with past dates
  useEffect(() => {
    if (!form.arrival_datetime) return;
    const todayStr = new Date().toISOString().slice(0, 10);
    const arrivalDateStr = form.arrival_datetime.slice(0, 10);
    if (arrivalDateStr < todayStr) {
      const freshForm = buildDefaultForm();
      setForm((prev) => ({ ...prev, arrival_datetime: freshForm.arrival_datetime, departure_datetime: freshForm.departure_datetime }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Pre-fill guest details from Guest Master "Re Check-In" — must be AFTER the property-reset effect above
  useEffect(() => {
    if (!prefillGuest) return;
    const fullName = [prefillGuest.title, prefillGuest.first_name, prefillGuest.last_name]
      .filter(v => String(v || '').trim()).join(' ');
    // Step 1 fields
    setForm(prev => ({
      ...prev,
      guest_name: fullName || prev.guest_name,
      mobile_number: prefillGuest.mobile_number || prev.mobile_number,
      email: prefillGuest.email || prev.email,
      gst_number: prefillGuest.gst_number || prev.gst_number,
      company_name: prefillGuest.company_name || prev.company_name,
    }));
    // Step 2 — compliance data pre-filled from guest master
    setComplianceGuestList([{
      guest_name: fullName,
      nationality: prefillGuest.nationality || selectedProperty?.country || 'India',
      is_foreign_guest: Boolean(prefillGuest.is_foreign_guest),
      gender: prefillGuest.gender || '',
      dob: prefillGuest.dob || '',
      address: prefillGuest.address || '',
      purpose_of_visit: prefillGuest.purpose_of_visit || '',
      id_proof_type: prefillGuest.id_proof_type || '',
      id_proof_no: prefillGuest.id_proof_no || '',
      id_expiry_date: prefillGuest.id_expiry_date || '',
      passport_number: prefillGuest.passport_number || '',
      visa_number: prefillGuest.visa_number || '',
      visa_expiry_date: prefillGuest.visa_expiry_date || '',
      arrival_stamp_date: prefillGuest.arrival_stamp_date || '',
      date_of_arrival_in_india: prefillGuest.date_of_arrival_in_india || '',
      place_of_issue: prefillGuest.place_of_issue || '',
      id_scan_data: prefillGuest.id_scan_data || '',
      id_scan_name: prefillGuest.id_scan_name || '',
      passport_scan_data: prefillGuest.passport_scan_data || '',
      passport_scan_name: prefillGuest.passport_scan_name || '',
    }]);
    if (typeof onPrefillGuestConsumed === 'function') onPrefillGuestConsumed();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefillGuest?.ts]);

  useEffect(() => {
    if (!lockedRoom?.room_id) return;
    const pickedRoom = rooms.find((r) => String(r.room_id) === String(lockedRoom.room_id));
    setForm((prev) => {
      const currentLine = prev.lines?.[0] || buildDefaultLine();
      const nextLine = {
        ...currentLine,
        room_id: String(lockedRoom.room_id),
        room_type_id: pickedRoom?.room_type_id ? String(pickedRoom.room_type_id) : currentLine.room_type_id
      };
      return { ...prev, lines: [nextLine] };
    });
  }, [lockedRoom?.room_id, lockedRoom?.ts, rooms]);

  const lineAssignedAdults = useMemo(
    () => form.lines.reduce((sum, l) => sum + Number(l.adults || 0), 0),
    [form.lines]
  );
  const lineAssignedChildren = useMemo(
    () => form.lines.reduce((sum, l) => sum + Number(l.children || 0), 0),
    [form.lines]
  );
  const lineAssignedTotalAmount = useMemo(
    () => form.lines.reduce((sum, l) => sum + Number(l.total_amount || 0), 0),
    [form.lines]
  );
  const walkInNights = useMemo(() => {
    if (!form.arrival_datetime || !form.departure_datetime) return 0;
    const arr = new Date(form.arrival_datetime);
    const dep = new Date(form.departure_datetime);
    if (Number.isNaN(arr.getTime()) || Number.isNaN(dep.getTime())) return 0;
    if (dep <= arr) return 0; // invalid range — same guard as the backend's real-timestamp check
    // Same-calendar-day walk-in (e.g. arrive 16:05, leave 23:59) still bills
    // a minimum one night, matching /walkin/rate-quote's billing floor.
    const dateOnlyNights = Math.round((new Date(dep).setHours(0,0,0,0) - new Date(arr).setHours(0,0,0,0)) / 86400000);
    return Math.max(1, dateOnlyNights);
  }, [form.arrival_datetime, form.departure_datetime]);

  const getAvailableRoomsForLine = (lineIndex, line) => {
    const selectedElsewhere = new Set(
      form.lines
        .map((l, idx) => (idx === lineIndex ? null : String(l.room_id || '')))
        .filter(Boolean)
    );
    return rooms.filter((r) => {
      if (selectedElsewhere.has(String(r.room_id))) return false;
      if (line.room_type_id && String(r.room_type_id) !== String(line.room_type_id)) return false;
      if (String(r.occupancy_status || '').toUpperCase() === 'OCCUPIED' && String(r.room_id) !== String(line.room_id)) return false;
      return true;
    });
  };

  const getPlansForLine = (line) => {
    if (!line.room_type_id) return [];
    return ratePlans.filter((p) => String(p.room_type_id || '') === String(line.room_type_id));
  };

  const getRoomTypeForLine = (line) => roomTypes.find((rt) => String(rt.room_type_id) === String(line.room_type_id));

  const getLineCapacity = (line) => {
    const roomType = getRoomTypeForLine(line);
    if (!roomType) {
      return { maxAdults: 1, maxChildren: 0, maxPax: 1 };
    }
    const maxAdults = Math.max(1, Number(roomType.max_adults || 1));
    const maxChildren = Math.max(0, Number(roomType.max_children || 0));
    const maxPax = Math.max(1, Number(roomType.max_pax || (maxAdults + maxChildren) || 1));
    return { maxAdults, maxChildren, maxPax };
  };

  const deriveQuoteForLine = async (lineIndex, nextForm, opts = {}) => {
    const line = nextForm.lines[lineIndex];
    const canQuote = selectedProperty?.id
      && line?.room_id
      && line?.rate_plan_code
      && nextForm.arrival_datetime
      && nextForm.departure_datetime;
    if (!canQuote) {
      setForm((prev) => {
        const lines = [...prev.lines];
        lines[lineIndex] = { ...lines[lineIndex], rate_amount: 0, tax_total: 0, total_amount: 0, nights: 0 };
        return { ...prev, lines };
      });
      return;
    }
    // keepOverride: an Include/Exclude switch re-reads the manually entered
    // per-night amount under the new mode instead of reverting to the plan rate.
    const overridePerNight = Number(opts.overridePerNight
      || (opts.keepOverride && line.rate_override && line.rate_override_per_night ? line.rate_override_per_night : 0));
    try {
      const res = await fetch('/api/v1/pms/walkin/rate-quote', {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify({
          room_id: Number(line.room_id),
          arrival_datetime: dateTimeLocalToIso(nextForm.arrival_datetime),
          departure_datetime: dateTimeLocalToIso(nextForm.departure_datetime),
          rate_plan_code: line.rate_plan_code,
          adults: Number(line.adults || 1),
          children: Number(line.children || 0),
          ...(overridePerNight > 0 ? { rate_override_per_night: overridePerNight } : {}),
          ...(line.tax_mode ? { tax_mode: line.tax_mode } : {})
        })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Failed to derive line quote');
      setForm((prev) => {
        const lines = [...prev.lines];
        lines[lineIndex] = {
          ...lines[lineIndex],
          tax_mode: lines[lineIndex].tax_mode || data?.tax_mode || '',
          rate_override_per_night: overridePerNight > 0 ? overridePerNight : undefined,
          rate_amount: Number(data?.subtotal || 0),
          tax_total: Number(data?.tax_total || 0),
          total_amount: Number(data?.total_amount || 0),
          nights: Number(data?.nights || 0),
          _orig_rate_amount: Number(data?.subtotal || 0),
          _orig_tax_total: Number(data?.tax_total || 0),
          rate_override: overridePerNight > 0
        };
        return { ...prev, lines };
      });
    } catch (_) {
      setForm((prev) => {
        const lines = [...prev.lines];
        lines[lineIndex] = { ...lines[lineIndex], rate_amount: 0, tax_total: 0, total_amount: 0, nights: 0 };
        return { ...prev, lines };
      });
    }
  };

  const updateLine = (lineIndex, patch) => {
    const nextForm = {
      ...form,
      lines: form.lines.map((line, idx) => (idx === lineIndex ? { ...line, ...patch } : line))
    };
    if (patch.room_type_id && String(patch.room_type_id) !== String(form.lines[lineIndex].room_type_id)) {
      nextForm.lines[lineIndex].room_id = '';
      nextForm.lines[lineIndex].rate_plan_code = '';
    }
    // A new rate plan starts from that plan's own Include/Exclude GST default.
    if (patch.rate_plan_code !== undefined && String(patch.rate_plan_code) !== String(form.lines[lineIndex].rate_plan_code || '')) {
      nextForm.lines[lineIndex].tax_mode = '';
    }
    if (patch.room_id) {
      const picked = rooms.find((r) => String(r.room_id) === String(patch.room_id));
      if (picked?.room_type_id) nextForm.lines[lineIndex].room_type_id = String(picked.room_type_id);
    }
    const selectedType = roomTypes.find((rt) => String(rt.room_type_id) === String(nextForm.lines[lineIndex].room_type_id));
    nextForm.lines[lineIndex] = clampLineByRoomType(nextForm.lines[lineIndex], selectedType);
    setForm(nextForm);
    // Skip rate-quote if user manually overrode the rate
    if (!patch.rate_override) {
      // If key fields changed, clear override and re-quote
      if (patch.room_type_id || patch.room_id || patch.rate_plan_code || patch.adults != null || patch.children != null) {
        nextForm.lines[lineIndex].rate_override = false;
        nextForm.lines[lineIndex]._orig_rate_amount = undefined;
        nextForm.lines[lineIndex]._orig_tax_total = undefined;
      }
      deriveQuoteForLine(lineIndex, nextForm, { keepOverride: patch.tax_mode !== undefined });
    }
  };

  useEffect(() => {
    form.lines.forEach((_, idx) => {
      deriveQuoteForLine(idx, form);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.arrival_datetime, form.departure_datetime]);

  // Numeric tail of a room number ("205" -> 205, "12A" -> 12) for proximity
  // scoring below. Mirrors backend/pmsRoomAssignUtils.js roomNumberDigits —
  // same "keep multi-room bookings together" heuristic OTA auto-assign uses.
  const roomNumberDigits = (roomNumber) => {
    const m = String(roomNumber || '').match(/\d+/);
    return m ? parseInt(m[0], 10) : null;
  };

  const scoreRoomCandidate = (candidate, siblingRooms) => {
    if (!siblingRooms.length) return 0;
    let best = -1;
    const candDigits = roomNumberDigits(candidate.room_number);
    for (const sib of siblingRooms) {
      let score = 0;
      if (candidate.block_name && sib.block_name && candidate.block_name === sib.block_name) score += 200;
      if (candidate.floor_no && sib.floor_no && candidate.floor_no === sib.floor_no) score += 100;
      const sibDigits = roomNumberDigits(sib.room_number);
      if (candDigits != null && sibDigits != null) score += Math.max(0, 50 - Math.abs(candDigits - sibDigits));
      if (score > best) best = score;
    }
    return Math.max(best, 0);
  };

  // Fills any line that has a room type but no room yet with the
  // best-scoring vacant room of that type, preferring rooms on the same
  // floor/block as a room already assigned in this booking. Never
  // overwrites a line already assigned — additive only. Builds the whole
  // next lines array in one pass (NOT via repeated updateLine calls, which
  // would each close over the same stale `form` and clobber each other)
  // then commits it in a single setForm, mirroring what updateLine itself
  // does — including re-deriving the rate quote for every line this just
  // touched. Every pick still goes through the backend's own validation
  // at submit time, same as picking it from the dropdown by hand.
  const autoAssignRooms = () => {
    const usedIds = new Set(form.lines.filter((l) => l.room_id).map((l) => String(l.room_id)));
    const claimed = form.lines
      .filter((l) => l.room_id)
      .map((l) => rooms.find((r) => String(r.room_id) === String(l.room_id)))
      .filter(Boolean);
    const touchedIdx = [];
    const nextLines = form.lines.map((line, idx) => {
      if (line.room_id || !line.room_type_id) return line;
      const candidates = getAvailableRoomsForLine(idx, line).filter((r) => !usedIds.has(String(r.room_id)));
      if (!candidates.length) return line;
      const ranked = candidates
        .map((c) => ({ c, score: scoreRoomCandidate(c, claimed) }))
        .sort((a, b) => b.score - a.score
          || String(a.c.room_number).localeCompare(String(b.c.room_number), undefined, { numeric: true }));
      const best = ranked[0].c;
      usedIds.add(String(best.room_id));
      claimed.push(best);
      touchedIdx.push(idx);
      const patched = { ...line, room_id: String(best.room_id), room_type_id: String(best.room_type_id) };
      const selectedType = roomTypes.find((rt) => String(rt.room_type_id) === String(patched.room_type_id));
      return clampLineByRoomType(patched, selectedType);
    });
    if (!touchedIdx.length) return;
    const nextForm = { ...form, lines: nextLines };
    setForm(nextForm);
    touchedIdx.forEach((idx) => deriveQuoteForLine(idx, nextForm));
  };

  const addRoomLine = () => {
    // Always add a line with blank room_type_id so all types are selectable
    setForm((prev) => ({ ...prev, lines: [...prev.lines, buildDefaultLine()] }));
  };

  const removeRoomLine = (lineIndex) => {
    setForm((prev) => {
      if (prev.lines.length <= 1) return prev;
      return { ...prev, lines: prev.lines.filter((_, idx) => idx !== lineIndex) };
    });
  };

  const addQuickRooms = () => {
    const { room_type_id, count, adults, children, rate_plan_code } = quickAdd;
    if (!room_type_id || count < 1) return;
    const requestedQty = Math.min(Math.max(1, Number(count)), 50);
    const totalAdults = Math.max(0, Number(adults || 0));
    const totalChildren = Math.max(0, Number(children || 0));

    // Auto-assign vacant rooms of the selected type, excluding already-used rooms
    const usedRoomIds = new Set(form.lines.map((l) => String(l.room_id || '')).filter(Boolean));
    const availableRooms = rooms
      .filter((r) => String(r.room_type_id) === String(room_type_id) && !usedRoomIds.has(String(r.room_id)));

    // Cap at available rooms — Walk-In requires actual room assignment
    const qty = Math.min(requestedQty, availableRooms.length);
    if (qty === 0) return;

    // Auto-pick the first rate plan for this room type if none selected
    const effectivePlan = rate_plan_code
      || (ratePlans.find((rp) => String(rp.room_type_id || '') === String(room_type_id))?.rate_plan_code)
      || '';

    const selectedType = roomTypes.find((rt) => String(rt.room_type_id) === String(room_type_id));
    const maxPax = selectedType ? Math.max(1, Number(selectedType.max_pax || 3)) : 99;
    const maxA = selectedType ? Math.max(1, Number(selectedType.max_adults || 2)) : 99;

    // Distribute adults evenly across rooms, clamped by capacity
    const baseAdults = qty > 0 ? Math.floor(totalAdults / qty) : 1;
    let remainderAdults = qty > 0 ? totalAdults - (baseAdults * qty) : 0;
    // Distribute children evenly across rooms
    const baseChildren = qty > 0 ? Math.floor(totalChildren / qty) : 0;
    let remainderChildren = qty > 0 ? totalChildren - (baseChildren * qty) : 0;

    const newLines = Array.from({ length: qty }, (_, i) => {
      let a = baseAdults + (remainderAdults > 0 ? 1 : 0);
      if (remainderAdults > 0) remainderAdults--;
      let c = baseChildren + (remainderChildren > 0 ? 1 : 0);
      if (remainderChildren > 0) remainderChildren--;
      const line = {
        ...buildDefaultLine(),
        room_type_id: String(room_type_id),
        room_id: String(availableRooms[i].room_id),
        adults: Math.max(1, a),
        children: Math.max(0, c),
        rate_plan_code: effectivePlan
      };
      return clampLineByRoomType(line, selectedType);
    });

    // Track requested totals for the remaining-pax banner
    setPaxTarget((prev) => ({
      adults: prev.adults + totalAdults,
      children: prev.children + totalChildren
    }));

    setForm((prev) => {
      const existingNonEmpty = prev.lines.filter((l) => l.room_type_id || l.room_id);
      const merged = existingNonEmpty.length ? [...prev.lines, ...newLines] : newLines;
      return { ...prev, lines: merged };
    });

    // Trigger rate quotes for new lines with assigned rooms
    setTimeout(() => {
      setForm((latestForm) => {
        const startIdx = latestForm.lines.length - qty;
        for (let i = startIdx; i < latestForm.lines.length; i++) {
          if (latestForm.lines[i]?.room_id && latestForm.lines[i]?.rate_plan_code) {
            deriveQuoteForLine(i, latestForm);
          }
        }
        return latestForm;
      });
    }, 100);

    setQuickAddOpen(false);
    setQuickAdd({ room_type_id: '', count: 1, adults: 2, children: 0, rate_plan_code: '' });
  };

  // Email validation function
  const isValidEmail = (email) => {
    if (!email) return true; // Allow empty (optional), else validate
    return /^[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,10}$/.test(email);
  };

  // Step 1 → Step 2 transition
  const goToComplianceStep = () => {
    if (!selectedProperty?.id) return setError('Select property first');
    if (!form.guest_name) return setError('Guest Name is required');
    const todayStr = new Date().toISOString().slice(0, 10);
    const arrivalDateStr = form.arrival_datetime ? form.arrival_datetime.slice(0, 10) : '';
    if (arrivalDateStr && arrivalDateStr < todayStr) {
      return setError('Check-In date cannot be in the past. Please update the Check-In Date & Time to today.');
    }
    const mobileError = validateMobile(form.mobile_number);
    if (mobileError) return setError(mobileError);
    if (!form.lines.length) return setError('At least one room is required');
    if (!isValidEmail(form.email)) return setError('Please enter a valid email address.');
    if (isRoomLockedFromContext && String(form.lines?.[0]?.room_id || '') !== String(lockedRoom.room_id)) {
      return setError(`Walk-In from Room View must check-in selected room ${lockedRoom.room_number || lockedRoom.room_id}`);
    }
    if (form.lines.some((l) => !l.room_id || !l.rate_plan_code)) return setError('Room and Rate Plan are required on every line');
    for (let i = 0; i < form.lines.length; i += 1) {
      const line = form.lines[i];
      const cap = getLineCapacity(line);
      if (Number(line.adults || 1) + Number(line.children || 0) > cap.maxPax)
        return setError(`Line ${i + 1}: total pax cannot exceed ${cap.maxPax}`);
    }
    setError('');
    const isEachGuest = policyConfig?.domestic_document_mode === 'EACH_GUEST';
    const guestList = buildGuestList(
      selectedProperty?.country,
      form.guest_name,
      lineAssignedAdults,
      isEachGuest
    );
    // Preserve pre-filled compliance data from Guest Master Re Check-In
    // Merge existing primary guest data (keeps id_proof, dob, gender, address, etc.)
    guestList[0] = { ...guestList[0], ...complianceGuestList[0], guest_name: form.guest_name };
    setComplianceGuestList(guestList);
    setActiveGuestIdx(0);
    setComplianceErrorsList(Array(guestList.length).fill({}));
    setStep(2);
  };

  const validateSingleGuest = (guestData, guestIdx) => {
    const hotelCountry = selectedProperty?.country || 'India';
    const normalizedHotelCountry = normalizeCountry(hotelCountry);
    const normalizedNationality = normalizeCountry(guestData.nationality);
    const isForeign = Boolean(guestData.is_foreign_guest) || (
      normalizedNationality
        ? (normalizedHotelCountry ? normalizedNationality !== normalizedHotelCountry : (normalizedNationality !== 'india' && normalizedNationality !== 'indian'))
        : false
    );
    const errs = {};
    // Additional guests require a name
    if (guestIdx > 0 && !guestData.guest_name) {
      errs.guest_name = 'Guest Name is required';
    }
    fieldRules.forEach((r) => {
      if (!r.is_mandatory || !r.is_visible) return;
      if (r.applies_to === 'FOREIGN' && !isForeign) return;
      if (r.applies_to === 'INDIAN' && isForeign) return;
      if (isForeign && (r.field_name === 'id_proof_type' || r.field_name === 'id_proof_no')) return;
      if (r.field_name === 'age_years') return; // age field removed
      if (!guestData[r.field_name]) {
        errs[r.field_name] = `${r.label || r.field_name} is required`;
      }
    });
    const todayStr = new Date().toISOString().slice(0, 10);
    if (isForeign) {
      if (guestData.visa_expiry_date && guestData.visa_expiry_date < todayStr) {
        errs.visa_expiry_date = 'Visa Expiry Date cannot be in the past';
      }
    } else {
      if (guestData.id_expiry_date && guestData.id_expiry_date < todayStr) {
        errs.id_expiry_date = 'ID Expiry Date cannot be in the past';
      }
    }
    return errs;
  };

  const validateComplianceFields = () => {
    // Only primary guest (index 0) is mandatory for check-in; additional guests are optional
    return complianceGuestList.map((guestData, idx) => {
      if (idx === 0) return validateSingleGuest(guestData, idx);
      return {}; // skip validation for additional guests
    });
  };

  const submit = async () => {
    const errorsList = validateComplianceFields();
    const totalMissing = errorsList.reduce((sum, e) => sum + Object.keys(e).length, 0);
    const hasMissing = totalMissing > 0;

    if (hasMissing && !overrideMode) {
      setComplianceErrorsList(errorsList);
      // Jump to first guest with errors
      const firstErrIdx = errorsList.findIndex((e) => Object.keys(e).length > 0);
      if (firstErrIdx >= 0) setActiveGuestIdx(firstErrIdx);
      return;
    }

    setError('');
    setMessage('');
    try {
      if (hasMissing && overrideMode) {
        if (!overrideReason.trim()) return setError('Please provide an override reason.');
        const allMissingFields = errorsList.flatMap((e) => Object.keys(e));
        await fetch('/api/v1/compliance/override', {
          method: 'POST',
          headers: headers(),
          body: JSON.stringify({
            override_reason: overrideReason,
            fields: allMissingFields
          })
        });
      }

      const [primaryGuest, ...additionalGuests] = complianceGuestList;

      // When override is active, suppress compliance fields that may be carrying
      // pre-filled values from a returning guest's prior stay (address, purpose,
      // ID, etc.). Without this, "skip ID" still saves stale data and the police
      // report shows it because the override stamp arrived too late to mask. The
      // backend also stamps compliance_override_reason on the new reservation.
      const COMPLIANCE_FIELDS_TO_BLANK = [
        'address', 'purpose_of_visit',
        'id_proof_type', 'id_proof_no', 'id_expiry_date',
        'passport_number', 'visa_number', 'visa_expiry_date',
        'arrival_stamp_date', 'date_of_arrival_in_india', 'place_of_issue',
        'id_scan_data', 'id_scan_name', 'passport_scan_data', 'passport_scan_name',
        'cform_submission_status'
      ];
      const sanitizeForOverride = (g) => {
        if (!g) return g;
        const out = { ...g };
        for (const f of COMPLIANCE_FIELDS_TO_BLANK) out[f] = null;
        return out;
      };
      const usingOverride = hasMissing && overrideMode;
      const cleanedPrimary = usingOverride ? sanitizeForOverride(primaryGuest) : primaryGuest;
      const cleanedAdditional = usingOverride ? additionalGuests.map(sanitizeForOverride) : additionalGuests;

      const payload = {
        arrival_datetime: dateTimeLocalToIso(form.arrival_datetime),
        departure_datetime: dateTimeLocalToIso(form.departure_datetime),
        guest_name: form.guest_name,
        mobile_number: form.mobile_number,
        email: form.email,
        gst_number: form.gst_number || null,
        company_name: form.company_name || null,
        total_pax: lineAssignedAdults + lineAssignedChildren,
        deposit_amount: Number(form.deposit_amount || 0),
        deposit_payment_mode: form.deposit_payment_mode || 'CASH',
        deposit_reference_no: form.deposit_reference_no || '',
        deposit_remarks: form.deposit_remarks,
        lines: form.lines.map((l) => ({
          room_id: Number(l.room_id),
          adults: Number(l.adults || 1),
          children: Number(l.children || 0),
          rate_plan_code: l.rate_plan_code,
          rate_amount: Number(l.rate_amount || 0),
          tax_amount: Number(l.tax_total || l.tax_amount || 0),
          tax_mode: l.tax_mode || undefined
        })),
        // Primary guest compliance fields
        ...cleanedPrimary,
        // Additional guests (sequence 2, 3, ...)
        additional_guests: cleanedAdditional.length > 0 ? cleanedAdditional : undefined,
        compliance_override_reason: usingOverride ? overrideReason.trim() : undefined
      };
      const res = await fetch('/api/v1/pms/walkin/checkin', {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify(payload)
      });
      const data = await res.json().catch(() => ({}));
      if (res.status === 409 && data?.restrictions?.length) {
        setRestrictionModal({ restrictions: data.restrictions, payload });
        return;
      }
      if (!res.ok) throw new Error(data?.error || 'Walk-in check-in failed');
      const firstRoomId = form.lines[0]?.room_id;
      const firstRoomNumber = rooms.find((r) => String(r.room_id) === String(firstRoomId))?.room_number || firstRoomId || '-';
      const guestPortalLink = buildGuestPortalUrl(selectedProperty?.code);
      setMessage(`Checked in successfully for room ${firstRoomNumber} . Reservation ${data.reservation_no}`);
      setGuestPortalUrl(guestPortalLink);
      // Open the lock-encode modal if there's a reservation_id we can target.
      // The modal handles its own offline-fallback path.
      if (data?.reservation_id) {
        setLockEncodeModal({
          reservationId: data.reservation_id,
          roomNumber: firstRoomNumber,
          guestName: form?.guest_name || form?.lines?.[0]?.guest_name || ''
        });
      }
      setForm(buildFormForCurrentContext());
      loadPmsConfig();
      setStep(1);
      setComplianceGuestList([buildDefaultComplianceData(selectedProperty?.country)]);
      setActiveGuestIdx(0);
      setComplianceErrorsList([{}]);
      setOverrideMode(false);
      setOverrideReason('');
      loadMasters();
    } catch (err) {
      setError(err?.message || 'Walk-in check-in failed');
    }
  };

  const submitWithRestrictionOverride = async () => {
    if (!restrictionModal) return;
    setRestrictionBusy(true);
    try {
      const overridePayload = { ...restrictionModal.payload, override_restrictions: true };
      const res = await fetch('/api/v1/pms/walkin/checkin', {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify(overridePayload)
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Override failed');
      setRestrictionModal(null);
      const firstRoomId = form.lines[0]?.room_id;
      const firstRoomNumber = rooms.find((r) => String(r.room_id) === String(firstRoomId))?.room_number || firstRoomId || '-';
      const guestPortalLink = buildGuestPortalUrl(selectedProperty?.code);
      setMessage(`Checked in (restrictions overridden) for room ${firstRoomNumber}. Reservation ${data.reservation_no}`);
      setGuestPortalUrl(guestPortalLink);
      setForm(buildFormForCurrentContext());
      loadPmsConfig();
      setStep(1);
      setComplianceGuestList([buildDefaultComplianceData(selectedProperty?.country)]);
      setActiveGuestIdx(0);
      setComplianceErrorsList([{}]);
      setOverrideMode(false);
      setOverrideReason('');
      loadMasters();
    } catch (err) {
      setError(err?.message || 'Override failed');
      setRestrictionModal(null);
    } finally {
      setRestrictionBusy(false);
    }
  };

  const missingCount = complianceErrorsList.reduce((sum, e) => sum + Object.keys(e).length, 0);

  return (
    <div className="page-shell">
      <h3 style={{ marginTop: 0, color: 'var(--theme-primary)' }}>Front Office - Walk-In Check-In</h3>

      {/* Step Indicator */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 0, marginBottom: '0.8rem' }}>
        {[{ n: 1, label: 'Guest & Room' }, { n: 2, label: 'ID Compliance' }].map((s, i) => (
          <React.Fragment key={s.n}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <div style={{ width: 26, height: 26, borderRadius: '50%', display: 'flex', alignItems: 'center',
                justifyContent: 'center', fontSize: 13, fontWeight: 700,
                background: step === s.n ? '#2563eb' : step > s.n ? 'var(--theme-success)' : '#e5e7eb',
                color: step >= s.n ? '#fff' : '#6b7280' }}>{step > s.n ? '✓' : s.n}</div>
              <span style={{ fontSize: 12, fontWeight: step === s.n ? 700 : 400,
                color: step === s.n ? '#2563eb' : '#6b7280' }}>{s.label}</span>
            </div>
            {i < 1 && <div style={{ flex: 1, height: 2, background: step > 1 ? 'var(--theme-success)' : '#e5e7eb',
              margin: '0 8px', minWidth: 30 }} />}
          </React.Fragment>
        ))}
      </div>

      {isRoomLockedFromContext ? (
        <div style={{ marginBottom: '0.5rem', color: 'var(--theme-primary)', fontSize: '0.84rem' }}>
          Selected Room Locked: <b>{lockedRoom?.room_number || lockedRoom?.room_id}</b>
        </div>
      ) : null}
      {error ? <div className="error-banner">{error}</div> : null}
      {message ? <div className="success-banner">{message}</div> : null}
      {guestPortalUrl && message && (
        <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '7px', padding: '0.6rem 0.8rem', marginBottom: '0.6rem', fontSize: '0.84rem' }}>
          <div style={{ fontWeight: 600, color: '#1e40af', marginBottom: '0.3rem' }}>Guest Portal Link</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <input readOnly value={guestPortalUrl} style={{ flex: 1, border: '1px solid #93c5fd', borderRadius: '4px', padding: '0.3rem 0.5rem', fontSize: '0.82rem', background: '#fff', color: '#1e3a5f' }} onClick={e => e.target.select()} />
            <button type="button" onClick={() => { navigator.clipboard.writeText(guestPortalUrl); }} style={{ background: '#2563eb', color: '#fff', border: 'none', borderRadius: '4px', padding: '0.3rem 0.7rem', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer' }}>Copy</button>
          </div>
          <div style={{ color: '#6b7280', fontSize: '0.75rem', marginTop: '0.25rem' }}>Share this link with the guest for room service, housekeeping & bill access</div>
        </div>
      )}
      {step === 1 && (
        <>
          <div
            style={{
              marginBottom: '0.55rem',
              fontSize: '0.82rem',
              border: `1px solid ${roomTaxPolicyConfigured ? '#9ae6b4' : '#fbd38d'}`,
              background: roomTaxPolicyConfigured ? '#f0fff4' : '#fffaf0',
              color: roomTaxPolicyConfigured ? '#22543d' : '#7b341e',
              borderRadius: '6px',
              padding: '0.4rem 0.55rem'
            }}
          >
            Tax Source: {roomTaxPolicyConfigured ? 'Hotel Room Tax Policy' : 'Rate Plan Tax Profile'}.
          </div>

          <div style={{ background: 'linear-gradient(180deg,#f8fbff 0%,#eef6ff 100%)', border: '1px solid #d7e3f4', borderRadius: '10px', padding: isMobile ? '0.6rem' : '0.8rem', display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit,minmax(190px,1fr))', gap: '0.5rem' }}>
            <label style={{ display: 'grid', gap: '0.22rem' }}>
              <span style={{ fontSize: '0.8rem', fontWeight: 600 }}>Check-In Date & Time</span>
              <input type="datetime-local" value={form.arrival_datetime}
                min={new Date().toISOString().slice(0, 10) + 'T00:00'}
                onChange={(e) => setForm({ ...form, arrival_datetime: e.target.value })}
                style={{ width: '100%', boxSizing: 'border-box' }} />
            </label>
            <label style={{ display: 'grid', gap: '0.22rem' }}>
              <span style={{ fontSize: '0.8rem', fontWeight: 600 }}>Expected Check-Out</span>
              <input type="datetime-local" value={form.departure_datetime} onChange={(e) => setForm({ ...form, departure_datetime: e.target.value })}
                style={{ width: '100%', boxSizing: 'border-box' }} />
            </label>
            <label style={{ display: 'grid', gap: '0.22rem' }}>
              <span style={{ fontSize: '0.8rem', fontWeight: 600 }}>Guest Name</span>
              <input placeholder="Guest Name" value={form.guest_name} onChange={(e) => { setError(''); setForm({ ...form, guest_name: e.target.value }); }} />
            </label>
            <label style={{ display: 'grid', gap: '0.22rem' }}>
              <span style={{ fontSize: '0.8rem', fontWeight: 600 }}>Mobile</span>
              <input
                placeholder="Mobile (10 digits)"
                value={form.mobile_number}
                maxLength={10}
                inputMode="numeric"
                onChange={(e) => {
                  const digits = e.target.value.replace(/\D/g, '').slice(0, 10);
                  setError('');
                  setForm({ ...form, mobile_number: digits });
                }}
              />
            </label>
            <label style={{ display: 'grid', gap: '0.22rem' }}>
              <span style={{ fontSize: '0.8rem', fontWeight: 600 }}>Email</span>
              <input placeholder="Email" value={form.email} onChange={(e) => { setError(''); setForm({ ...form, email: e.target.value }); }} />
            </label>
            <label style={{ display: 'grid', gap: '0.22rem' }}>
              <span style={{ fontSize: '0.8rem', fontWeight: 600 }}>Customer GSTIN <span style={{ fontWeight: 400, color: '#9ca3af' }}>(optional)</span></span>
              <input placeholder="15-digit GSTIN" value={form.gst_number || ''} onChange={(e) => setForm({ ...form, gst_number: e.target.value.toUpperCase().replace(/[^0-9A-Z]/g, '').slice(0, 15) })} style={{ fontFamily: 'monospace' }} />
            </label>
            <label style={{ display: 'grid', gap: '0.22rem' }}>
              <span style={{ fontSize: '0.8rem', fontWeight: 600 }}>Company Name <span style={{ fontWeight: 400, color: '#9ca3af' }}>(optional)</span></span>
              <input placeholder="Company / Trade name" value={form.company_name || ''} onChange={(e) => setForm({ ...form, company_name: e.target.value })} />
            </label>
            <label style={{ display: 'grid', gap: '0.22rem' }}>
              <span style={{ fontSize: '0.8rem', fontWeight: 600 }}>Advance Amount</span>
              <input type="text" inputMode="decimal" value={form.deposit_amount} onChange={(e) => setForm({ ...form, deposit_amount: e.target.value })} />
            </label>
            <label style={{ display: 'grid', gap: '0.22rem' }}>
              <span style={{ fontSize: '0.8rem', fontWeight: 600 }}>Payment Mode</span>
              <select value={form.deposit_payment_mode} onChange={(e) => setForm({ ...form, deposit_payment_mode: e.target.value })}>
                <option value="CASH">Cash</option>
                <option value="CARD">Card</option>
                <option value="UPI">UPI</option>
                <option value="BANK_TRANSFER">Bank Transfer</option>
                <option value="CHEQUE">Cheque</option>
                <option value="WALLET">Wallet</option>
              </select>
            </label>
            <label style={{ display: 'grid', gap: '0.22rem' }}>
              <span style={{ fontSize: '0.8rem', fontWeight: 600 }}>Reference No</span>
              <input placeholder="Txn / Receipt No" value={form.deposit_reference_no} onChange={(e) => setForm({ ...form, deposit_reference_no: e.target.value })} />
            </label>
            <label style={{ display: 'grid', gap: '0.22rem' }}>
              <span style={{ fontSize: '0.8rem', fontWeight: 600 }}>Advance Remarks</span>
              <input value={form.deposit_remarks} onChange={(e) => setForm({ ...form, deposit_remarks: e.target.value })} />
            </label>
          </div>

          <div style={{ marginTop: '0.8rem', border: '1px solid #d7e3f4', borderRadius: '10px', overflow: 'hidden' }}>
            <div style={{ padding: '0.6rem 0.8rem', background: '#eaf4ff', color: 'var(--theme-primary)', fontWeight: 700, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>Add Room Lines</span>
              <div style={{ display: 'flex', gap: '0.4rem' }}>
                {!isRoomLockedFromContext && (
                  <button type="button" onClick={() => setQuickAddOpen((v) => !v)}
                    style={{ background: '#eef2ff', color: '#3730a3', border: '1px solid #c7d2fe', borderRadius: 6,
                      padding: '3px 10px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                    {quickAddOpen ? '− Quick Add' : '+ Quick Add'}
                  </button>
                )}
                {!isRoomLockedFromContext ? <button onClick={addRoomLine}>Add Room</button> : null}
                {!isRoomLockedFromContext && (
                  <button
                    type="button"
                    onClick={autoAssignRooms}
                    title="Fills empty rows with the best available room, keeping multi-room bookings on the same floor/block"
                    style={{ background: '#eef2ff', color: '#3730a3', border: '1px solid #c7d2fe', borderRadius: 6,
                      padding: '3px 10px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
                  >
                    Auto-Assign Rooms
                  </button>
                )}
              </div>
            </div>
            {quickAddOpen && !isRoomLockedFromContext && (
              <div style={{ padding: '0.6rem 0.8rem', background: '#f5f7ff', borderBottom: '1px solid #d7e3f4',
                display: isMobile ? 'grid' : 'flex', gridTemplateColumns: isMobile ? '1fr 1fr' : undefined, flexWrap: 'wrap', gap: '0.5rem', alignItems: 'flex-end' }}>
                <label style={{ display: 'flex', flexDirection: 'column', fontSize: 11, fontWeight: 600, color: 'var(--theme-text)', gridColumn: isMobile ? '1 / -1' : undefined }}>
                  Room Type
                  <select value={quickAdd.room_type_id}
                    onChange={(e) => {
                      const rtId = e.target.value;
                      const rt = roomTypes.find((t) => String(t.room_type_id) === String(rtId));
                      setQuickAdd((p) => ({
                        ...p,
                        room_type_id: rtId,
                        rate_plan_code: '',
                        adults: Number(p.count || 1) * Math.min(2, rt ? Math.max(1, Number(rt.max_adults || 2)) : 2),
                        children: 0
                      }));
                    }}
                    style={{ marginTop: 2, minWidth: isMobile ? 0 : 130, width: isMobile ? '100%' : undefined }}>
                    <option value="">Select</option>
                    {roomTypes.map((rt) => <option key={rt.room_type_id} value={rt.room_type_id}>{rt.room_type_name}</option>)}
                  </select>
                </label>
                <label style={{ display: 'flex', flexDirection: 'column', fontSize: 11, fontWeight: 600, color: 'var(--theme-text)' }}>
                  No. of Rooms
                  <input type="number" min={1} max={50} value={quickAdd.count}
                    onChange={(e) => setQuickAdd((p) => ({ ...p, count: e.target.value }))}
                    style={{ marginTop: 2, width: isMobile ? '100%' : 55 }} />
                </label>
                <label style={{ display: 'flex', flexDirection: 'column', fontSize: 11, fontWeight: 600, color: 'var(--theme-text)' }}>
                  Total Adults
                  <input type="number" min={0} max={999} value={quickAdd.adults}
                    onChange={(e) => setQuickAdd((p) => ({ ...p, adults: e.target.value }))}
                    style={{ marginTop: 2, width: isMobile ? '100%' : 60 }} />
                </label>
                <label style={{ display: 'flex', flexDirection: 'column', fontSize: 11, fontWeight: 600, color: 'var(--theme-text)' }}>
                  Total Children
                  <input type="number" min={0} max={999} value={quickAdd.children}
                    onChange={(e) => setQuickAdd((p) => ({ ...p, children: e.target.value }))}
                    style={{ marginTop: 2, width: isMobile ? '100%' : 60 }} />
                </label>
                <label style={{ display: 'flex', flexDirection: 'column', fontSize: 11, fontWeight: 600, color: 'var(--theme-text)', gridColumn: isMobile ? '1 / -1' : undefined }}>
                  Rate Plan
                  <select value={quickAdd.rate_plan_code}
                    onChange={(e) => setQuickAdd((p) => ({ ...p, rate_plan_code: e.target.value }))}
                    style={{ marginTop: 2, minWidth: isMobile ? 0 : 120, width: isMobile ? '100%' : undefined }}>
                    <option value="">Select</option>
                    {ratePlans
                      .filter((rp) => !quickAdd.room_type_id || String(rp.room_type_id || '') === String(quickAdd.room_type_id))
                      .map((rp) => (
                        <option key={rp.rate_plan_id} value={rp.rate_plan_code}>{rp.rate_plan_code} - {rp.rate_plan_name}</option>
                      ))}
                  </select>
                </label>
                {(() => {
                  const usedIds = new Set(form.lines.map((l) => String(l.room_id || '')).filter(Boolean));
                  const availCount = quickAdd.room_type_id
                    ? rooms.filter((r) => String(r.room_type_id) === String(quickAdd.room_type_id) && !usedIds.has(String(r.room_id)) && String(r.occupancy_status || '').toUpperCase() !== 'OCCUPIED').length
                    : 0;
                  const requested = Math.min(Math.max(1, Number(quickAdd.count) || 1), 50);
                  const effectiveQty = Math.min(requested, availCount);
                  const noRooms = !quickAdd.room_type_id || availCount === 0;
                  return (
                    <>
                      <button type="button" disabled={noRooms || quickAdd.count < 1}
                        onClick={addQuickRooms}
                        style={{ background: '#4f46e5', color: '#fff', border: 'none', borderRadius: 6,
                          padding: '5px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer',
                          opacity: (noRooms || quickAdd.count < 1) ? 0.5 : 1 }}>
                        Add {effectiveQty || requested} Room{effectiveQty !== 1 ? 's' : ''}
                      </button>
                      {quickAdd.room_type_id && requested > availCount && (
                        <span style={{ fontSize: 11, color: availCount === 0 ? 'var(--theme-danger)' : '#b45309', fontWeight: 600 }}>
                          {availCount === 0 ? 'No vacant rooms available' : `Only ${availCount} vacant room${availCount !== 1 ? 's' : ''} available — will add ${availCount}`}
                        </span>
                      )}
                    </>
                  );
                })()}
              </div>
            )}
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: 'var(--theme-bg-light)' }}>
                    <th style={{ padding: '0.45rem', textAlign: 'left' }}>Room Type</th>
                    <th style={{ padding: '0.45rem', textAlign: 'left' }}>Room</th>
                    <th style={{ padding: '0.45rem', textAlign: 'left' }}>Adults</th>
                    <th style={{ padding: '0.45rem', textAlign: 'left' }}>Children</th>
                    <th style={{ padding: '0.45rem', textAlign: 'left' }}>Rate Plan</th>
                    <th style={{ padding: '0.45rem', textAlign: 'left' }} title="Include: Rate already contains GST. Exclude: GST is added on top.">GST Incl / Excl</th>
                    <th style={{ padding: '0.45rem', textAlign: 'right' }}>Rate</th>
                    <th style={{ padding: '0.45rem', textAlign: 'right' }}>Tax</th>
                    <th style={{ padding: '0.45rem', textAlign: 'right' }}>Total</th>
                    <th style={{ padding: '0.45rem', textAlign: 'center' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {form.lines.map((line, idx) => {
                    const roomOptions = getAvailableRoomsForLine(idx, line);
                    const planOptions = getPlansForLine(line);
                    const cap = getLineCapacity(line);
                    const adultOptions = buildCountOptions(1, cap.maxPax);
                    const childOptions = buildCountOptions(0, cap.maxPax);
                    const linePax = Number(line.adults || 0) + Number(line.children || 0);
                    const extraAdults = Math.max(0, Number(line.adults || 0) - cap.maxAdults);
                    const extraChildren = Math.max(0, Number(line.children || 0) - cap.maxChildren);
                    const linePlan = ratePlans.find((rp) => String(rp.rate_plan_code) === String(line.rate_plan_code));
                    const lineTaxMode = line.tax_mode || (linePlan?.tax_inclusive ? 'INCLUSIVE' : 'EXCLUSIVE');
                    const isInclusive = lineTaxMode === 'INCLUSIVE';
                    // Inclusive: Rate shows the GST-inclusive stay amount (rate + tax).
                    const displayRate = Number(line.rate_amount || 0) + (isInclusive ? Number(line.tax_total || 0) : 0);
                    const origDisplayRate = Number(line._orig_rate_amount ?? line.rate_amount ?? 0)
                      + (isInclusive ? Number(line._orig_tax_total ?? line.tax_total ?? 0) : 0);
                    return (
                      <tr key={`line-${idx}`} style={{ borderTop: '1px solid var(--theme-border)' }}>
                        <td style={{ padding: '0.45rem' }}>
                          <select value={line.room_type_id} onChange={(e) => updateLine(idx, { room_type_id: e.target.value })} disabled={isRoomLockedFromContext && idx === 0}>
                            <option value="">Select Room Type</option>
                            {roomTypes.map((rt) => <option key={rt.room_type_id} value={rt.room_type_id}>{rt.room_type_name}</option>)}
                          </select>
                        </td>
                        <td style={{ padding: '0.45rem' }}>
                          <select value={line.room_id} onChange={(e) => updateLine(idx, { room_id: e.target.value })} disabled={isRoomLockedFromContext && idx === 0}>
                            <option value="">Select Room</option>
                            {roomOptions.map((r) => <option key={r.room_id} value={r.room_id}>{r.room_number}{String(r.occupancy_status || '').toUpperCase() === 'OCCUPIED' ? ' (Occupied)' : ''}</option>)}
                          </select>
                        </td>
                        <td style={{ padding: '0.45rem' }}>
                          <select value={line.adults} onChange={(e) => updateLine(idx, { adults: Number(e.target.value || 1) })}>
                            {adultOptions.map((count) => <option key={`ad-${idx}-${count}`} value={count}>{count}</option>)}
                          </select>
                        </td>
                        <td style={{ padding: '0.45rem' }}>
                          <select value={line.children} onChange={(e) => updateLine(idx, { children: Number(e.target.value || 0) })}>
                            {childOptions.map((count) => <option key={`ch-${idx}-${count}`} value={count}>{count}</option>)}
                          </select>
                        </td>
                        <td style={{ padding: '0.45rem' }}>
                          <select value={line.rate_plan_code} onChange={(e) => updateLine(idx, { rate_plan_code: e.target.value })}>
                            <option value="">Select Rate Plan</option>
                            {planOptions.map((rp) => (
                              <option key={rp.rate_plan_id} value={rp.rate_plan_code}>{rp.rate_plan_code} - {rp.rate_plan_name}</option>
                            ))}
                          </select>
                        </td>
                        <td style={{ padding: '0.45rem' }}>
                          <select
                            value={lineTaxMode}
                            disabled={!line.rate_plan_code}
                            onChange={(e) => updateLine(idx, { tax_mode: e.target.value })}
                          >
                            <option value="EXCLUSIVE">Exclude</option>
                            <option value="INCLUSIVE">Include</option>
                          </select>
                        </td>
                        <td style={{ padding: '0.45rem', textAlign: 'right' }}>
                          <input type="text" inputMode="decimal"
                            defaultValue={displayRate > 0 ? displayRate.toFixed(2) : ''}
                            key={`wrate-${idx}-${line._orig_rate_amount || ''}-${lineTaxMode}`}
                            placeholder="0.00"
                            style={{ width: 90, textAlign: 'right', padding: '0.25rem 0.4rem', border: '1px solid #cbd5e0', borderRadius: 4, fontSize: '0.85rem', fontFamily: 'monospace' }}
                            onBlur={(e) => {
                              const val = e.target.value.replace(/[^0-9.]/g, '');
                              const newRate = Number(val || 0);
                              // No change → nothing to do
                              if (newRate === Number(origDisplayRate.toFixed(2))) return;
                              const n = Math.max(1, Number(line.nights || 0));
                              const perNight = newRate / n;
                              // Re-quote on the server using the manual per-night tariff so the
                              // correct tax slab / tax percent is applied (matters most when the
                              // rate plan has no calendar rates).
                              if (perNight > 0) {
                                deriveQuoteForLine(idx, form, { overridePerNight: perNight });
                              } else {
                                deriveQuoteForLine(idx, form);
                              }
                            }}
                          />
                        </td>
                        <td style={{ padding: '0.45rem', textAlign: 'right' }}>
                          {Number(line.tax_total || 0).toFixed(2)}
                          {isInclusive && Number(line.tax_total || 0) > 0 ? (
                            <div style={{ fontSize: '0.7rem', color: '#718096' }}>incl. in rate</div>
                          ) : null}
                        </td>
                        <td style={{ padding: '0.45rem', textAlign: 'right', fontWeight: 600 }}>{Number(line.total_amount || 0).toFixed(2)}</td>
                        <td style={{ padding: '0.45rem', textAlign: 'center' }}>
                          <div style={{ fontSize: '0.75rem', color: linePax > cap.maxPax ? '#c53030' : '#475569', marginBottom: '0.2rem' }}>
                            Pax {linePax}/{cap.maxPax} | Extra A:{extraAdults} C:{extraChildren}
                          </div>
                          <button disabled={form.lines.length <= 1 || (isRoomLockedFromContext && idx === 0)} onClick={() => removeRoomLine(idx)}>Remove</button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <div style={{ marginTop: '0.6rem', display: 'flex', gap: '1rem', flexWrap: 'wrap', fontWeight: 600 }}>
            <span>Assigned Adults: {lineAssignedAdults}</span>
            <span>Assigned Children: {lineAssignedChildren}</span>
            <span>Nights: {walkInNights}</span>
            <span>Grand Total: {lineAssignedTotalAmount.toFixed(2)}</span>
          </div>

          {/* Booking Summary */}
          {lineAssignedTotalAmount > 0 && walkInNights > 0 && (
            <div style={{ marginTop: '0.5rem', background: '#ebf8ff', border: '1px solid #bee3f8', borderRadius: '7px', padding: '0.6rem 0.8rem' }}>
              <div style={{ fontWeight: 700, fontSize: '0.88rem', color: '#2b6cb0', marginBottom: '0.4rem' }}>Booking Summary</div>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ borderCollapse: 'collapse', fontSize: '0.83rem', width: '100%' }}>
                  <thead>
                    <tr style={{ background: '#bee3f8' }}>
                      <th style={{ padding: '0.3rem 0.6rem', textAlign: 'left' }}>Room</th>
                      <th style={{ padding: '0.3rem 0.6rem', textAlign: 'right' }}>Nights</th>
                      <th style={{ padding: '0.3rem 0.6rem', textAlign: 'right' }}>Rate / Night</th>
                      <th style={{ padding: '0.3rem 0.6rem', textAlign: 'right' }}>Room Charges</th>
                      <th style={{ padding: '0.3rem 0.6rem', textAlign: 'right' }}>Tax</th>
                      <th style={{ padding: '0.3rem 0.6rem', textAlign: 'right' }}>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {form.lines.map((line, idx) => {
                      const nights = Number(line.nights || walkInNights);
                      const rateAmt = Number(line.rate_amount || 0);
                      const taxAmt = Number(line.tax_total || 0);
                      const lineTotal = Number(line.total_amount || 0);
                      const ratePerNight = nights > 0 ? rateAmt / nights : rateAmt;
                      const roomObj = rooms.find(r => String(r.room_id) === String(line.room_id));
                      return (
                        <tr key={idx} style={{ borderBottom: '1px solid #bee3f8' }}>
                          <td style={{ padding: '0.28rem 0.6rem', color: '#4a5568' }}>{roomObj?.room_number || `Room ${idx + 1}`}</td>
                          <td style={{ padding: '0.28rem 0.6rem', textAlign: 'right' }}>{nights}</td>
                          <td style={{ padding: '0.28rem 0.6rem', textAlign: 'right' }}>{ratePerNight.toFixed(2)}</td>
                          <td style={{ padding: '0.28rem 0.6rem', textAlign: 'right' }}>{rateAmt.toFixed(2)}</td>
                          <td style={{ padding: '0.28rem 0.6rem', textAlign: 'right', color: '#c05621' }}>{taxAmt.toFixed(2)}</td>
                          <td style={{ padding: '0.28rem 0.6rem', textAlign: 'right', fontWeight: 600 }}>{lineTotal.toFixed(2)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr style={{ background: '#2b6cb0', color: '#fff', fontWeight: 700 }}>
                      <td style={{ padding: '0.35rem 0.6rem' }} colSpan={3}>Grand Total</td>
                      <td style={{ padding: '0.35rem 0.6rem', textAlign: 'right' }}>{form.lines.reduce((s, l) => s + Number(l.rate_amount || 0), 0).toFixed(2)}</td>
                      <td style={{ padding: '0.35rem 0.6rem', textAlign: 'right' }}>{form.lines.reduce((s, l) => s + Number(l.tax_total || 0), 0).toFixed(2)}</td>
                      <td style={{ padding: '0.35rem 0.6rem', textAlign: 'right' }}>{lineAssignedTotalAmount.toFixed(2)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          )}

          {/* Remaining pax banner — shows when Quick Add pax > assigned pax */}
          {paxTarget.adults > 0 && (paxTarget.adults - lineAssignedAdults > 0 || paxTarget.children - lineAssignedChildren > 0) && (
            <div style={{ marginTop: '0.5rem', padding: '0.45rem 0.7rem', background: '#fff7ed', border: '1px solid #fdba74',
              borderRadius: 6, fontSize: 12, color: '#92400e', fontWeight: 600 }}>
              {paxTarget.adults - lineAssignedAdults > 0 && (
                <span>{paxTarget.adults - lineAssignedAdults} adult{paxTarget.adults - lineAssignedAdults !== 1 ? 's' : ''} unassigned (requested {paxTarget.adults}, placed {lineAssignedAdults}). </span>
              )}
              {paxTarget.children - lineAssignedChildren > 0 && (
                <span>{paxTarget.children - lineAssignedChildren} child{paxTarget.children - lineAssignedChildren !== 1 ? 'ren' : ''} unassigned (requested {paxTarget.children}, placed {lineAssignedChildren}). </span>
              )}
              <span>Add more rooms or adjust counts in existing rooms.</span>
            </div>
          )}

          <div style={{ marginTop: '0.7rem', display: 'flex', gap: '0.5rem' }}>
            <button onClick={goToComplianceStep} style={{ background: '#2563eb', color: '#fff', border: 'none',
              borderRadius: 6, padding: '7px 20px', fontWeight: 600, cursor: 'pointer' }}>
              Next: ID Compliance →
            </button>
            <button onClick={() => { setForm(buildFormForCurrentContext()); setStep(1); setPaxTarget({ adults: 0, children: 0 }); }}>Clear</button>
          </div>
        </>
      )}

      {/* Step 2: Compliance */}
      {step === 2 && (
        <div style={{ background: '#f8faff', border: '1px solid #d7e3f4', borderRadius: 10, padding: '1rem', marginTop: '0.4rem' }}>
          <div style={{ marginBottom: 12, fontSize: 13, color: 'var(--theme-text)' }}>
            Guest: <b>{form.guest_name}</b> &nbsp;|&nbsp; Mobile: <b>{form.mobile_number}</b>
            &nbsp;|&nbsp; Adults: <b>{lineAssignedAdults}</b>
            {complianceGuestList.length > 1 && (
              <span style={{ marginLeft: 8, padding: '2px 8px', background: '#dcfce7', color: '#166534',
                borderRadius: 10, fontSize: 11, fontWeight: 600 }}>
                Primary guest ID required for check-in. Guest 2-{complianceGuestList.length} optional — can be added later.
              </span>
            )}
          </div>

          {/* Guest Tabs (shown only when multiple guests) */}
          {complianceGuestList.length > 1 && (
            <div style={{ display: 'flex', gap: 4, marginBottom: 14, flexWrap: 'wrap' }}>
              {complianceGuestList.map((g, idx) => {
                const hasErr = idx === 0 && Object.keys(complianceErrorsList[idx] || {}).length > 0;
                const isActive = idx === activeGuestIdx;
                const isFilled = idx > 0 && g && (g.id_proof_no || g.id_proof_type || g.gender || g.dob);
                return (
                  <button key={idx} onClick={() => setActiveGuestIdx(idx)}
                    style={{
                      padding: '5px 14px', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                      border: isActive ? '2px solid #2563eb' : '1px solid #d1d5db',
                      background: isActive ? '#eff6ff' : hasErr ? '#fef2f2' : isFilled ? '#f0fdf4' : '#fff',
                      color: isActive ? '#1e40af' : hasErr ? 'var(--theme-danger)' : isFilled ? '#166534' : 'var(--theme-text)',
                      position: 'relative'
                    }}>
                    {idx === 0 ? 'Guest 1 (Primary)' : `Guest ${idx + 1}`}
                    {idx > 0 && !isFilled && <span style={{ marginLeft: 4, fontSize: 10, color: '#9ca3af' }}>(Optional)</span>}
                    {isFilled && <span style={{ marginLeft: 4, color: 'var(--theme-success)' }}>&#10003;</span>}
                    {hasErr && <span style={{ marginLeft: 4, color: 'var(--theme-danger)' }}>&#9888;</span>}
                  </button>
                );
              })}
            </div>
          )}

          <PmsComplianceCheckin
            values={complianceGuestList[activeGuestIdx] || {}}
            onChange={(name, val) => setComplianceGuestList((prev) => {
              const next = [...prev];
              next[activeGuestIdx] = { ...next[activeGuestIdx], [name]: val };
              return next;
            })}
            fieldRules={fieldRules}
            errors={complianceErrorsList[activeGuestIdx] || {}}
            policyConfig={policyConfig}
            countryOptions={countryOptions}
            idTypeOptions={idTypeOptions}
            hotelCountry={selectedProperty?.country || 'India'}
            showGuestName={activeGuestIdx > 0}
          />

          {/* Guest Intelligence Card (AI) */}
          {aiGuestRecognitionEnabled && activeGuestIdx === 0 && form.mobile_number && (
            <GuestIntelligenceCard
              mobileNumber={form.mobile_number}
              chainId={selectedProperty?.chain_id}
              propertyId={selectedProperty?.id}
              token={token}
            />
          )}

          {/* Guest navigation arrows (when multiple guests) */}
          {complianceGuestList.length > 1 && (
            <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
              {activeGuestIdx > 0 && (
                <button onClick={() => setActiveGuestIdx((i) => i - 1)}
                  style={{ padding: '5px 14px', background: '#e5e7eb', color: 'var(--theme-text)', border: 'none',
                    borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                  ← Previous Guest
                </button>
              )}
              {activeGuestIdx < complianceGuestList.length - 1 && (
                <button onClick={() => setActiveGuestIdx((i) => i + 1)}
                  style={{ padding: '5px 14px', background: '#2563eb', color: '#fff', border: 'none',
                    borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                  Next Guest →
                </button>
              )}
            </div>
          )}

          {missingCount > 0 && (
            <div style={{ marginTop: 14, padding: '10px 14px', background: '#fffbeb',
              border: '1px solid #fbbf24', borderRadius: 8 }}>
              <div style={{ fontSize: 13, color: '#b45309', fontWeight: 600, marginBottom: 6 }}>
                {missingCount} required field{missingCount > 1 ? 's are' : ' is'} missing for primary guest.
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
                <input type='checkbox' checked={overrideMode}
                  onChange={(e) => { setOverrideMode(e.target.checked); setOverrideReason(''); }} />
                Override with reason (manager approval)
              </label>
              {overrideMode && (
                <textarea value={overrideReason} onChange={(e) => setOverrideReason(e.target.value)}
                  placeholder='Enter override reason (required)'
                  rows={2} style={{ width: '100%', marginTop: 8, padding: '6px 8px', fontSize: 13,
                    border: '1px solid #d1d5db', borderRadius: 6, boxSizing: 'border-box' }} />
              )}
            </div>
          )}

          <div style={{ marginTop: 14, display: 'flex', gap: 8 }}>
            <button onClick={() => { setStep(1); setError(''); }}
              className="btn-muted" style={{ padding: '7px 18px', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
              ← Back
            </button>
            <button onClick={submit}
              className="btn-success" style={{ padding: '7px 22px', borderRadius: 6, fontWeight: 600, cursor: 'pointer', fontSize: 13 }}>
              Check-In
            </button>
          </div>
        </div>
      )}
      {/* ── Lock-Encode Modal (post check-in card encoding) ── */}
      {lockEncodeModal && (
        <LockEncodeModal
          token={token}
          selectedProperty={selectedProperty}
          reservationId={lockEncodeModal.reservationId}
          roomNumber={lockEncodeModal.roomNumber}
          guestName={lockEncodeModal.guestName}
          onClose={() => setLockEncodeModal(null)}
        />
      )}
      {/* ── Inventory Restriction Warning Modal ── */}
      {restrictionModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 70 }}>
          <div style={{ width: 'min(540px,94vw)', background: '#fff', borderRadius: 10, padding: '1.3rem', position: 'relative', maxHeight: '80vh', overflowY: 'auto' }}>
            <button onClick={() => setRestrictionModal(null)} style={{ position: 'absolute', top: '0.5rem', right: '0.6rem', border: '1px solid #cbd5e0', background: '#fff', borderRadius: 4, padding: '0.05rem 0.4rem', fontSize: '1rem', cursor: 'pointer' }}>×</button>
            <h4 style={{ margin: '0 0 0.6rem', color: '#b45309', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
              <span style={{ fontSize: '1.2rem' }}>⚠</span> Inventory Restrictions
            </h4>
            <p style={{ margin: '0 0 0.7rem', fontSize: '0.85rem', color: '#6b7280' }}>
              The following restrictions are in effect for this check-in:
            </p>
            <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 6, padding: '0.7rem 0.9rem', marginBottom: '1rem', maxHeight: '40vh', overflowY: 'auto' }}>
              {restrictionModal.restrictions.map((r, i) => (
                <div key={i} style={{ display: 'flex', gap: '0.5rem', alignItems: 'baseline', padding: '0.25rem 0', borderBottom: i < restrictionModal.restrictions.length - 1 ? '1px solid #fef3c7' : 'none', fontSize: '0.84rem' }}>
                  <span style={{ background: r.type === 'SOLD_OUT' ? '#fee2e2' : r.type === 'STOP_SELL' ? '#fecaca' : '#e0e7ff', color: r.type === 'SOLD_OUT' ? '#991b1b' : r.type === 'STOP_SELL' ? '#b91c1c' : '#3730a3', fontWeight: 700, fontSize: '0.7rem', padding: '0.1rem 0.4rem', borderRadius: 999, whiteSpace: 'nowrap' }}>{r.type}</span>
                  <span style={{ color: 'var(--theme-text)' }}>{r.message}</span>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.6rem' }}>
              <button onClick={() => setRestrictionModal(null)} style={{ padding: '0.45rem 1.1rem', borderRadius: 5, border: '1px solid #d1d5db', background: '#fff', cursor: 'pointer', color: 'var(--theme-text)', fontWeight: 600 }}>Cancel</button>
              {['admin', 'hotel_admin'].includes(role) && (
                <button onClick={submitWithRestrictionOverride} disabled={restrictionBusy}
                  style={{ padding: '0.45rem 1.1rem', borderRadius: 5, border: 'none', background: '#b45309', color: '#fff', cursor: 'pointer', fontWeight: 700, fontSize: '0.85rem' }}>
                  {restrictionBusy ? 'Saving...' : 'Override & Check-In'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PmsWalkInCheckIn;
